import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { HeartHandshake, Gift, ArrowRight, Share2, Link2, Facebook, Twitter, Check } from "lucide-react";

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const MILESTONE_STEP = 25; // a "round milestone" every 25 families helped

const gentleConfetti = (big = false) => {
  confetti({
    particleCount: big ? 140 : 70,
    spread: big ? 95 : 70,
    startVelocity: big ? 45 : 34,
    origin: { y: 0.4 },
    scalar: 0.9,
    ticks: 220,
    colors: ["#8E9A86", "#C4A4A4", "#D9CBA0", "#B4894A", "#FDF6E3"],
    disableForReducedMotion: true,
  });
};

// Animated impact band: two stats count up from 0 when scrolled into view,
// with a gentle confetti burst on a round milestone or when the total has grown
// since the visitor's last visit (i.e. a new sale happened).
export function ImpactCounter({ target = 0, kits = 0, duration = 2000 }) {
  const [fam, setFam] = useState(0);
  const [kit, setKit] = useState(0);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);
  const started = useRef(false);

  const shareUrl = typeof window !== "undefined" ? window.location.origin : "https://hopeandfavor.com";
  const shareText = `Together, Hope & Favor has comforted ${target}+ families and sent ${kits} Hope Kits to people in their hardest moments 💛 Join the movement:`;
  const openPopup = (href) => window.open(href, "_blank", "noopener,noreferrer,width=600,height=560");
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Hope & Favor — our impact", text: shareText, url: shareUrl }); } catch { /* cancelled */ } }
    else { copyLink(); }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setCopied(true);
      toast.success("Copied — share the hope 💛");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Couldn't copy."); }
  };
  const shareFb = () => openPopup(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`);
  const shareX = () => openPopup(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const celebrate = () => {
      if (reduced) return;
      const readInt = (k) => { try { return parseInt(localStorage.getItem(k) || "", 10); } catch { return NaN; } };
      const famSeen = readInt("hf_impact_seen");
      const kitSeen = readInt("hf_kits_seen");
      const famGrew = Number.isFinite(famSeen) && target > famSeen;   // a new sale since last visit
      const kitGrew = Number.isFinite(kitSeen) && kits > kitSeen;     // a new Hope Kit since last visit
      const famMilestone = target > 0 && target % MILESTONE_STEP === 0;
      const kitMilestone = kits > 0 && kits % MILESTONE_STEP === 0;
      const milestone = famMilestone || kitMilestone;
      if (milestone || famGrew || kitGrew) {
        gentleConfetti(milestone);
        setTimeout(() => gentleConfetti(false), 220);
      }
      try {
        localStorage.setItem("hf_impact_seen", String(target));
        localStorage.setItem("hf_kits_seen", String(kits));
      } catch { /* ignore */ }
    };

    const run = () => {
      if (started.current) return;
      started.current = true;
      if (reduced) { setFam(target); setKit(kits); celebrate(); return; }
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - start) / duration);
        const e = easeOutCubic(p);
        setFam(Math.round(e * target));
        setKit(Math.round(e * kits));
        if (p < 1) requestAnimationFrame(tick);
        else celebrate();
      };
      requestAnimationFrame(tick);
    };

    const obs = new IntersectionObserver(
      (entries) => entries.forEach((en) => en.isIntersecting && run()),
      { threshold: 0.35 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [target, kits, duration]);

  return (
    <section ref={ref} data-testid="impact-counter-section" className="relative overflow-hidden bg-[#8E9A86]">
      {/* soft depth layers */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#8E9A86] via-[#7F8C77] to-[#6E7A67]" />
      <div
        className="absolute inset-0 opacity-[0.10]"
        style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #FAF7F2 1px, transparent 0)", backgroundSize: "22px 22px" }}
      />
      <div className="absolute -top-24 -right-16 h-72 w-72 rounded-full bg-[#D9CBA0]/20 blur-3xl" />
      <div className="absolute -bottom-28 -left-10 h-72 w-72 rounded-full bg-[#FAF7F2]/10 blur-3xl" />

      <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-24 sm:py-28 text-center">
        <div className="inline-flex items-center justify-center h-16 w-16 gh-octagon bg-[#FAF7F2]/15 ring-1 ring-[#FAF7F2]/30 mb-7">
          <HeartHandshake className="h-7 w-7 text-[#FAF7F2]" />
        </div>
        <span className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-[#FDF6E3]">Comfort delivered, hope shared</span>

        {/* two stats */}
        <div className="mt-9 grid sm:grid-cols-2 gap-10 sm:gap-0 items-center">
          <div className="sm:pr-8" data-testid="impact-stat-families">
            <div className="flex items-baseline justify-center gap-2">
              <span
                data-testid="impact-counter-number"
                className="font-heading leading-none text-[#FDF6E3] text-7xl sm:text-8xl tabular-nums tracking-tight drop-shadow-[0_4px_20px_rgba(26,26,26,0.25)]"
              >
                {fam.toLocaleString()}
              </span>
              <span className="font-heading text-[#D9CBA0] text-4xl sm:text-5xl leading-none">+</span>
            </div>
            <h2 className="font-heading italic text-2xl sm:text-3xl text-[#FAF7F2] mt-3">Families helped</h2>
          </div>

          <div className="sm:border-l sm:border-[#FDF6E3]/25 sm:pl-8" data-testid="impact-stat-kits">
            <div className="flex items-center justify-center gap-3">
              <Gift className="h-8 w-8 sm:h-9 sm:w-9 text-[#D9CBA0] shrink-0" />
              <span
                data-testid="impact-kits-number"
                className="font-heading leading-none text-[#FDF6E3] text-7xl sm:text-8xl tabular-nums tracking-tight drop-shadow-[0_4px_20px_rgba(26,26,26,0.25)]"
              >
                {kit.toLocaleString()}
              </span>
            </div>
            <h2 className="font-heading italic text-2xl sm:text-3xl text-[#FAF7F2] mt-3">Hope Kits sent</h2>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-[#FDF6E3]/90" data-testid="impact-counting">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#D9CBA0] opacity-70 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#D9CBA0]" />
          </span>
          <span className="font-hand text-2xl sm:text-3xl leading-none">and counting&hellip;</span>
        </div>

        <div className="h-px w-24 bg-[#D9CBA0]/50 mx-auto my-8" />
        <p className="text-[#F3EFE6]/90 text-base sm:text-lg leading-relaxed font-light max-w-xl mx-auto">
          Every box is a reminder that no one walks through their hardest season alone — and each one is made possible by people like you.
        </p>
        <Link
          to="/kit-finder"
          data-testid="join-movement-btn"
          className="group inline-flex items-center gap-2 mt-9 bg-[#FAF7F2] text-[#3A3632] rounded-full px-9 py-4 font-medium text-base shadow-[0_14px_40px_rgba(26,26,26,0.22)] hover:bg-white hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
        >
          Join the movement
          <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
        </Link>

        <div className="mt-8" data-testid="impact-share">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#FDF6E3]/80 mb-3">Share our impact</p>
          <div className="flex items-center justify-center gap-2.5">
            <button onClick={nativeShare} data-testid="impact-share-native"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#FAF7F2] rounded-full border border-[#FDF6E3]/50 px-4 h-10 hover:bg-[#FDF6E3]/15 transition-colors">
              <Share2 className="h-4 w-4" /> Share
            </button>
            {[
              { fn: copyLink, testid: "impact-share-copy", label: "Copy link", icon: copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" /> },
              { fn: shareFb, testid: "impact-share-facebook", label: "Share on Facebook", icon: <Facebook className="h-4 w-4" /> },
              { fn: shareX, testid: "impact-share-x", label: "Share on X", icon: <Twitter className="h-4 w-4" /> },
            ].map((b) => (
              <button key={b.testid} onClick={b.fn} aria-label={b.label} title={b.label} data-testid={b.testid}
                className="h-10 w-10 inline-flex items-center justify-center rounded-full border border-[#FDF6E3]/50 text-[#FAF7F2] hover:bg-[#FDF6E3]/15 transition-colors">
                {b.icon}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
